import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

/**
 * Strategy di riuso delle rotte.
 *
 * Le rotte marcate con `data: { reuseKey: '<id>' }` (es. la vista `issue` che contiene
 * board/list/calendar/map) vengono STACCATE e conservate quando si naviga altrove
 * (es. verso `issue-detail/:issueId`) invece di essere distrutte. Al ritorno il
 * sottoalbero viene RIATTACCATO senza ricostruirlo: niente re-subscribe, niente
 * re-layout delle card, niente debounce iniziale. Mentre è staccato il componente
 * resta vivo ma FUORI dal change detection (nessun re-render).
 *
 * La chiave include il projectId così boards di progetti diversi non si sovrappongono.
 * Tutte le altre rotte mantengono il comportamento di default (nessun riuso).
 */
export class AppRouteReuseStrategy implements RouteReuseStrategy {
  private handlers = new Map<string, DetachedRouteHandle>();

  private key(route: ActivatedRouteSnapshot): string | null {
    const reuseKey = route.routeConfig?.data?.['reuseKey'];
    if (!reuseKey) {
      return null;
    }
    // paramsInheritanceStrategy: 'always' → il projectId del parent è disponibile qui
    const projectId = route.paramMap.get('projectId') ?? '';
    return `${reuseKey}::${projectId}`;
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.key(route) !== null;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.key(route);
    if (!key) {
      return;
    }
    if (handle) {
      this.handlers.set(key, handle);
    } else {
      this.handlers.delete(key);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.key(route);
    return key !== null && this.handlers.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.key(route);
    if (!key) {
      return null;
    }
    return this.handlers.get(key) ?? null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }

  /** Svuota i sottoalberi conservati (es. al logout / cambio utente). */
  clear(): void {
    this.handlers.clear();
  }
}
